/**
 * Azure Application Insights Service
 *
 * This service provides a centralized interface for Application Insights telemetry.
 * It initializes the SDK and provides methods for custom event tracking.
 */

import { ApplicationInsights } from "@microsoft/applicationinsights-web"
import { appInsightsConfig, isAppInsightsEnabled } from "@/config/appInsightsConfig"

class AppInsightsService {
  private appInsights: ApplicationInsights | null = null
  private isInitialized = false

  /**
   * Initialize Application Insights
   * Call this once during app startup
   */
  initialize(): void {
    if (this.isInitialized) {
      console.warn("Application Insights is already initialized")
      return
    }

    if (!isAppInsightsEnabled()) {
      console.info(
        "Application Insights is disabled (no connection string provided)"
      )
      return
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: appInsightsConfig,
      })

      this.appInsights.loadAppInsights()

      // Add custom properties to all telemetry
      this.appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.data) {
          envelope.data.appVersion = import.meta.env.VITE_APP_VERSION || "unknown"
          envelope.data.environment = import.meta.env.MODE
        }
      })

      // Track initial page view
      this.appInsights.trackPageView()

      this.isInitialized = true
      console.info("Application Insights initialized successfully")
    } catch (error) {
      console.error("Failed to initialize Application Insights:", error)
    }
  }

  /**
   * Track a custom event
   * @param name Event name
   * @param properties Custom properties
   * @param measurements Custom measurements (numeric values)
   */
  trackEvent(
    name: string,
    properties?: Record<string, string>,
    measurements?: Record<string, number>
  ): void {
    if (!this.appInsights) return
    this.appInsights.trackEvent(
      { name, properties, measurements }
    )
  }

  /**
   * Track a page view
   * @param name Page name
   * @param uri Page URI
   */
  trackPageView(name?: string, uri?: string): void {
    if (!this.appInsights) return
    this.appInsights.trackPageView({ name, uri })
  }

  /**
   * Track an exception
   * @param error Error object
   * @param severityLevel Severity level (0-4)
   */
  trackException(error: Error, severityLevel?: number): void {
    if (!this.appInsights) return
    this.appInsights.trackException({
      exception: error,
      severityLevel,
    })
  }

  /**
   * Track a metric
   * @param name Metric name
   * @param average Average value
   * @param properties Custom properties
   */
  trackMetric(
    name: string,
    average: number,
    properties?: Record<string, string>
  ): void {
    if (!this.appInsights) return
    this.appInsights.trackMetric({ name, average }, properties)
  }

  /**
   * Set authenticated user context
   * @param userId User ID
   * @param accountId Account ID (optional)
   */
  setAuthenticatedUserContext(userId: string, accountId?: string): void {
    if (!this.appInsights) return
    this.appInsights.setAuthenticatedUserContext(userId, accountId, true)
  }

  /**
   * Clear authenticated user context
   */
  clearAuthenticatedUserContext(): void {
    if (!this.appInsights) return
    this.appInsights.clearAuthenticatedUserContext()
  }

  /**
   * Start tracking a timed event
   * @param name Event name
   * @returns Stop function to call when event completes
   */
  startTrackEvent(name: string): () => void {
    if (!this.appInsights) return () => {}
    this.appInsights.startTrackEvent(name)
    return () => this.stopTrackEvent(name)
  }

  /**
   * Stop tracking a timed event
   * @param name Event name
   * @param properties Custom properties
   * @param measurements Custom measurements
   */
  stopTrackEvent(
    name: string,
    properties?: Record<string, string>,
    measurements?: Record<string, number>
  ): void {
    if (!this.appInsights) return
    this.appInsights.stopTrackEvent(name, properties, measurements)
  }

  /**
   * Flush any batched telemetry
   */
  flush(): void {
    if (!this.appInsights) return
    this.appInsights.flush()
  }

  /**
   * Check if Application Insights is initialized and enabled
   */
  isEnabled(): boolean {
    return this.isInitialized && !!this.appInsights
  }
}

// Export singleton instance
export const appInsightsService = new AppInsightsService()
